/**
 * Session code generator for Teaching Facilitation.
 *
 * Format: ANIMAL-NATURE-NN  (e.g. "FOX-PINE-42")
 *
 * Readable, writable on a sticky note, low collision risk for beta.
 * ~20 × 20 × 90 = 36,000 combinations. Database insert retries on conflict.
 */

const ANIMALS = [
  'FOX', 'OWL', 'BEAR', 'WOLF', 'DEER', 'HARE', 'LYNX', 'MOLE',
  'DUCK', 'CROW', 'FROG', 'SEAL', 'OTTER', 'HAWK', 'ROBIN', 'SWAN',
  'RAVEN', 'FINCH', 'BADGER', 'BEAVER',
] as const;

const NATURE = [
  'PINE', 'OAK', 'MOSS', 'LEAF', 'FERN', 'RIVER', 'STONE', 'CLOUD',
  'BIRCH', 'ACORN', 'WILLOW', 'MAPLE', 'CEDAR', 'ROOT', 'STREAM',
  'HILL', 'ROSE', 'LILY', 'CLOVER', 'MEADOW',
] as const;

function pick<T>(list: readonly T[]): T {
  return list[Math.floor(Math.random() * list.length)];
}

/**
 * Generate one candidate code. Collisions are possible but rare — callers
 * should retry on database insert conflict.
 */
export function generateCode(): string {
  const animal = pick(ANIMALS);
  const nature = pick(NATURE);
  const number = 10 + Math.floor(Math.random() * 90); // 10-99
  return `${animal}-${nature}-${number}`;
}

/**
 * Accept user-typed codes loosely — normalize case and whitespace,
 * then validate against the expected format. Does NOT validate the
 * words are in our known lists (we keep that liberal so old codes
 * from earlier word lists still resolve).
 */
export function normalizeCode(input: string): string {
  return input.trim().toUpperCase().replace(/\s+/g, '-').replace(/-+/g, '-');
}

export function isValidCodeFormat(input: string): boolean {
  const code = normalizeCode(input);
  return /^[A-Z]{2,10}-[A-Z]{2,10}-\d{2}$/.test(code);
}
